/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/control-has-associated-label */

import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container } from 'src/components/layouts/Container/Container';
import { useTranslation } from 'react-i18next';
import Icon from 'src/components/ui/Icon/Icon';
import CardContainer from 'src/components/layouts/CardContainer/CardContainer';
import { ControlledSelect } from 'src/components/ui/Select/Select';
import { useForm } from 'react-hook-form';
import {
  useReportsQuery,
  useReportTypeTableQuery,
  Report,
  useAttachmentsBulk,
  useCreateReportByType,
} from 'src/services/reports';
import { getAvailableTimePeriods, formatDate, calculateDatesFromPeriod } from 'src/utils/general';
import { TFunction } from 'i18next';
import { createColumnHelper } from '@tanstack/react-table';
import { DatePicker } from 'src/components/ui/DatePicker/DatePicker';
import { downloadAttachment } from 'src/services/utility';
import dayjs, { Dayjs } from 'dayjs';
import { toast } from 'react-toastify';
import { useNavigationItems } from 'src/hooks/useNavigationItems';
import Button, { ButtonToneProps } from '../components/ui/Button/Button';
import { TableOverview } from '../components/ui/TableOverview/TableOverview';

const columnHelper = createColumnHelper<any>();
interface ColumnHelperProps {
  t: TFunction;
}

function AdvancedReportType() {
  const { t } = useTranslation();
  const { type } = useParams();

  const { data: reportData } = useReportsQuery({
    domain: `[ ("code", "ilike", "${type}")]`,
  });
  const report = reportData?.items[0];
  const { mutateAsync: getReportsAttachments } = useAttachmentsBulk();

  const { findAccessRights } = useNavigationItems();
  const accessRights = findAccessRights('advanced-reports');

  const [customStartDate, setCustomStartDate] = useState<Dayjs | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Dayjs | null>(null);

  const { control, watch } = useForm({
    defaultValues: {
      selectedPeriod: 'last_7_days',
    },
  });

  const selectedPeriod = watch('selectedPeriod');
  const { mutateAsync: createReport, isLoading } = useCreateReportByType(type || '');

  const createAndDownloadReport = async () => {
    let dates;

    if (selectedPeriod === 'custom') {
      if (!customStartDate || !customEndDate) {
        return;
      }
      dates = {
        start_date: customStartDate.format('YYYY-MM-DD'),
        end_date: customEndDate.format('YYYY-MM-DD'),
      };
    } else {
      dates = calculateDatesFromPeriod(selectedPeriod);
    }
    try {
      await createReport(dates);
      toast.success(t('success.report_created'));
    } catch (error) {
      console.error(error);
      toast.error(t('errors.report_creation_failed'));
    }
  };

  const getColumns = ({ t }: ColumnHelperProps) => [
    columnHelper.accessor(row => row.name, {
      id: 'name',
      size: 240,
      enableHiding: false,
      enableSorting: true,
      enableResizing: false,
      meta: {
        label: t('general.name'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex items-center w-full max-w-[240px]">
          <span className="typo-text-14--bold text-wrap truncate">{info.getValue()}</span>
        </div>
      ),
    }),
    columnHelper.accessor(row => row.from_date, {
      id: 'from_date',
      size: 320,
      enableHiding: false,
      enableSorting: false,
      enableResizing: false,
      meta: {
        label: t('reports.time_period'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex items-center w-full">
          <span className="typo-text-14">{`${formatDate(info.getValue())} — ${formatDate(info.row.original.to_date)}`}</span>
        </div>
      ),
    }),
    columnHelper.accessor(row => row.attachment_id, {
      id: 'attachment_id',
      size: 280,
      enableHiding: false,
      enableSorting: false,
      header: () => {},
      cell: info => (
        <div
          role="button"
          className="flex w-full items-center justify-end cursor-pointer"
          tabIndex={0}
          onClick={() => {
            downloadAttachment(info.getValue());
          }}>
          <div className="flex items-center gap-[8px]">
            <Icon name="download" width={16} height={16} className="text-primary" />
            <span className="text-primary typo-text-12--bold">{t('inbound.download')}</span>
          </div>
        </div>
      ),
    }),
  ];
  const isCoverageReport = report?.code?.toLowerCase().includes('coverage');

  return (
    <Container title={t('navbar.advanced_reports')}>
      <div className="flex flex-col gap-10">
        <h1 className="typo-title-36">{report?.name}</h1>
        <p className="typo-text-14 text-faded max-w-[410px] mb-[24px]">{report?.description}</p>
        <CardContainer isRow className="flex flex-col w-full items-start gap-20">
          {accessRights?.read_write && (
            <div className={`flex w-full justify-between ${isCoverageReport ? '' : 'flex-col'}`}>
              {isCoverageReport ? (
                <div className="flex items-end">
                  <Button
                    title={isLoading ? t('reports.downloading') : t('inbound.download')}
                    tone={ButtonToneProps.PRIMARY_BLUE}
                    icon="download"
                    onClick={createAndDownloadReport}
                    disabled={isLoading}
                  />
                </div>
              ) : (
                <>
                  <div className="typo-text-16--bold">{t('settings.create_report')}</div>
                  <CardContainer
                    isRow
                    className="flex flex-col w-full items-start gap-[12px] h-[110px] !overflow-visible mt-8">
                    <div className="flex items-end w-full justify-between gap-10">
                      <div className="flex w-full gap-10">
                        <div className="flex flex-col items-start gap-12 w-full max-w-[210px]">
                          <span className="typo-text-14--bold text-faded">{t('reports.time_period')}</span>

                          <ControlledSelect
                            name="selectedPeriod"
                            control={control}
                            options={getAvailableTimePeriods(t)}
                            className="w-full"
                          />
                        </div>

                        {selectedPeriod === 'custom' && (
                          <div className="flex items-center gap-10 w-full">
                            <DatePicker
                              label={t('reports.start_date')}
                              defaultValue={customStartDate}
                              onChange={date => setCustomStartDate(date ? dayjs(date) : null)}
                              maxDate={dayjs()}
                            />

                            <DatePicker
                              label={t('reports.end_date')}
                              defaultValue={customEndDate}
                              onChange={date => setCustomEndDate(date ? dayjs(date) : null)}
                              minDate={customStartDate || undefined}
                              maxDate={dayjs()}
                            />
                          </div>
                        )}
                      </div>

                      <Button
                        title={isLoading ? t('reports.downloading') : t('inbound.download')}
                        tone={ButtonToneProps.PRIMARY_BLUE}
                        icon="download"
                        onClick={createAndDownloadReport}
                        disabled={isLoading}
                      />
                    </div>
                  </CardContainer>
                </>
              )}
            </div>
          )}
          <div className="typo-text-16--bold mt-[16px]">{t('reports.archive')}</div>
          <div className="w-full">
            <TableOverview<Report>
              withSaveFilters={false}
              applyContainer={false}
              filterModelId="df.report"
              displayFilterRow={false}
              displayActionsColumn={false}
              displayColumnSelect={false}
              allowBulkActions={accessRights?.read_write}
              noResultsLabel={t('reports.no_report_archive')}
              actions={[
                {
                  title: 'Sammel-Download starten',
                  isRowClickAction: false,
                  showInContextMenu: false,
                  bulkSupport: true,
                  onExecute: async selected => {
                    const ids = selected.map(s => s.attachment_id).filter(id => id !== undefined && id !== null);
                    await getReportsAttachments({ model: 'df.report', ids });
                  },
                },
              ]}
              columns={getColumns({ t })}
              useDataQuery={useReportTypeTableQuery(type || '')}
            />
          </div>
        </CardContainer>
      </div>
    </Container>
  );
}

export default AdvancedReportType;
